import { DateFormatter, ZonedDateTime } from "@internationalized/date";
import { useLocale } from "react-aria";
import { DateSegment } from "react-aria-components";
import { ComponentProps, useMemo } from "react";
import { formatInTimeZone } from "date-fns-tz";

import styles from "./styles.module.css";

type CustomDateSegmentProps = {
  segment: ComponentProps<typeof DateSegment>["segment"];
  value: ZonedDateTime;
};

const CustomDateSegment = ({ segment, value }: CustomDateSegmentProps) => {
  const { locale } = useLocale();

  const dayPeriodFormatter = useMemo(
    () =>
      new DateFormatter(locale, {
        hour: "numeric",
        minute: "2-digit",
        timeZone: value.timeZone,
      }),
    [locale, value.timeZone]
  );

  if (segment.type === "timeZoneName") {
    return (
      <span className={styles.segmentStatic} data-type={segment.type}>
        {formatInTimeZone(value.toDate(), value.timeZone, "zzz")}
      </span>
    );
  }

  if (segment.type === "dayPeriod") {
    const parts = dayPeriodFormatter.formatToParts(value.toDate());
    const dayPeriodPart = parts.find((p) => p.type === "dayPeriod");
    if (!dayPeriodPart) {
      return <DateSegment className={styles.segment} segment={segment} />;
    }

    const isAM = value.hour < 12;
    let text = dayPeriodPart.value;
    if (/^AM|PM$/i.test(dayPeriodPart.value.trim())) {
      text = isAM ? "AM" : "PM";
    }

    return (
      <DateSegment
        className={styles.segment}
        segment={{
          ...segment,
          text,
          isPlaceholder: false,
        }}
      />
    );
  }

  return <DateSegment className={styles.segment} segment={segment} />;
};

CustomDateSegment.displayName = "DS.CustomDateSegment";

export default CustomDateSegment;
